import { Children, forwardRef, isValidElement } from "react";

import { Banner, ErrorMessage } from "@hireart/component-library";
import clsx from "clsx";
import PropTypes from "prop-types";

/**
 * SUI props that should not be forwarded to the design system components.
 */
const SUI_PROPS = [
  "icon",
  "size",
  "compact",
  "floating",
  "attached",
  "visible",
  "hidden",
  "list",
  "color",
  "error",
  "info",
  "success",
  "warning",
  "negative",
  "positive",
  "header",
  "content",
  "onDismiss",
];

const stripSuiProps = (props) => {
  const cleaned = { ...props };
  SUI_PROPS.forEach((key) => delete cleaned[key]);
  return cleaned;
};

const resolveVariant = ({
  error,
  negative,
  info,
  warning,
  success,
  positive,
}) => {
  if (error || negative) return "error";
  if (info) return "info";
  if (warning) return "warning";
  if (success || positive) return "success";
  return "default";
};

const MessageHeader = ({ children, className, ...rest }) => (
  <div className={clsx("body-2 mb-1 font-semibold", className)} {...rest}>
    {children}
  </div>
);

MessageHeader.displayName = "MessageHeader";
MessageHeader.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
};

const MessageContent = ({ children, className, ...rest }) => (
  <div className={clsx("body-2", className)} {...rest}>
    {children}
  </div>
);

MessageContent.displayName = "MessageContent";
MessageContent.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
};

const collectSubComponents = (children) => {
  let headerContent = null;
  let bodyContent = null;
  const otherChildren = [];

  Children.forEach(children, (child) => {
    if (!isValidElement(child)) {
      otherChildren.push(child);
      return;
    }
    if (
      child.type === MessageHeader ||
      child.type?.displayName === "MessageHeader"
    ) {
      headerContent = child.props.children;
    } else if (
      child.type === MessageContent ||
      child.type?.displayName === "MessageContent"
    ) {
      bodyContent = child.props.children;
    } else {
      otherChildren.push(child);
    }
  });

  return { headerContent, bodyContent, otherChildren };
};

const MessageList = ({ children, className, ...rest }) => (
  <ul className={className} {...rest}>
    {children}
  </ul>
);

MessageList.displayName = "MessageList";
MessageList.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
};

export const Message = forwardRef(function Message(props, ref) {
  const {
    children,
    className,
    header,
    content,
    error,
    negative,
    info,
    warning,
    success,
    positive,
    hidden,
    ...rest
  } = props;

  // SUI parity: semantic-ui Message suppressed rendering when `hidden` was set
  if (hidden) return null;

  const variant = resolveVariant({
    error,
    negative,
    info,
    warning,
    success,
    positive,
  });
  const cleanProps = stripSuiProps(rest);
  const { headerContent, bodyContent, otherChildren } =
    collectSubComponents(children);

  const resolvedHeader = header || headerContent;
  const resolvedBody = content || bodyContent;

  if (variant === "error") {
    return (
      <ErrorMessage
        ref={ref}
        className={className}
        heading={resolvedHeader}
        description={
          resolvedBody || otherChildren.length > 0 ? (
            <div>
              {resolvedBody}
              {otherChildren}
            </div>
          ) : undefined
        }
        {...cleanProps}
      />
    );
  }

  return (
    <Banner ref={ref} className={className} variant={variant} {...cleanProps}>
      <div>
        {resolvedHeader && (
          <div className="body-2 mb-1 font-semibold">{resolvedHeader}</div>
        )}
        {resolvedBody && <div className="body-2">{resolvedBody}</div>}
        {otherChildren}
      </div>
    </Banner>
  );
});

Message.displayName = "Message";

/* eslint-disable react/boolean-prop-naming -- SUI compatibility props */
Message.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  content: PropTypes.node,
  header: PropTypes.node,
  error: PropTypes.bool,
  negative: PropTypes.bool,
  info: PropTypes.bool,
  warning: PropTypes.bool,
  success: PropTypes.bool,
  positive: PropTypes.bool,
  icon: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.string,
    PropTypes.element,
  ]),
  size: PropTypes.string,
  compact: PropTypes.bool,
  floating: PropTypes.bool,
  attached: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  visible: PropTypes.bool,
  hidden: PropTypes.bool,
  list: PropTypes.arrayOf(PropTypes.node),
  color: PropTypes.string,
  onDismiss: PropTypes.func,
};
/* eslint-enable react/boolean-prop-naming */

Message.Header = MessageHeader;
Message.Content = MessageContent;
Message.List = MessageList;
